import React, { useState, useEffect, useContext, useMemo, useCallback } from "react";
import {
  Typography,
  Input,
  Button,
  Dropdown,
  Menu,
  Icon,
  message,
} from "antd";
import moment from "moment";
import { platformApi } from "../../../../api";
import { ContextAPI } from "../../../../ContextAPI";
import { useBranch } from "../../../../ContextAPI/branch";
import StockCheckTable from "./StockCheckTable";
import StockInwardModal from "./StockInwardModal";
import StockInwardViewModal from "./StockInwardViewModal";

const { Item } = Menu;
const { Title } = Typography;
const { Search } = Input;

const StockCheck = () => {
  const { loginCredintials } = useContext(ContextAPI);
  const { selectBranch } = useBranch();

  // Active Selected Branch(es) from top-left selector / localStorage
  const activeBranchIds = useMemo(() => {
    if (Array.isArray(selectBranch) && selectBranch.length > 0) {
      return selectBranch
        .map((b) => (typeof b === "string" ? b : b.id || b._id))
        .filter(Boolean);
    }
    try {
      const stored = localStorage.getItem("branch");
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed) && parsed.length > 0) {
          return parsed
            .map((b) => (typeof b === "string" ? b : b.id || b._id))
            .filter(Boolean);
        }
      }
    } catch (e) {
      console.warn("Could not parse stored branch ids:", e);
    }
    return [];
  }, [selectBranch]);

  const activeBranchNames = useMemo(() => {
    if (Array.isArray(selectBranch) && selectBranch.length > 0) {
      return selectBranch
        .map((b) => (typeof b === "string" ? b : b.name))
        .filter(Boolean)
        .map((n) => n.trim().toLowerCase());
    }
    try {
      const stored = localStorage.getItem("branch");
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed) && parsed.length > 0) {
          return parsed
            .map((b) => (typeof b === "string" ? b : b.name))
            .filter(Boolean)
            .map((n) => n.trim().toLowerCase());
        }
      }
    } catch (e) {
      console.warn("Could not parse stored branch names:", e);
    }
    return [];
  }, [selectBranch]);

  // Permissions
  const [createAccess, setCreateAccess] = useState(false);
  const [modifyAccess, setModifyAccess] = useState(false);
  const [deleteAccess, setDeleteAccess] = useState(false);

  // List Data & Pagination States
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [count, setCount] = useState(0);

  // Search Filter
  const [searchQuery, setSearchQuery] = useState("");

  // Master Data
  const [branches, setBranches] = useState([]);
  const [masterBinLocations, setMasterBinLocations] = useState([]);
  const [usersMap, setUsersMap] = useState({});

  // Modals
  const [isInwardModalOpen, setIsInwardModalOpen] = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [viewRecord, setViewRecord] = useState(null);

  // 1. Role-based permissions
  useEffect(() => {
    if (loginCredintials && loginCredintials.roleAccess) {
      loginCredintials.roleAccess.forEach((each) => {
        const key = (each.subModule || "").toLowerCase().replace(/[-_]/g, "");
        if (["stockcheck", "stockcity", "stockinward", "stockchecking"].includes(key)) {
          if (each.access?.create) setCreateAccess(true);
          if (each.access?.update) setModifyAccess(true);
          if (each.access?.delete) setDeleteAccess(true);
        }
      });
    }
  }, [loginCredintials]);

  // 2. Fetch accessible branches
  useEffect(() => {
    const fetchBranches = async () => {
      try {
        const storedBranch = localStorage.getItem("branch");
        if (storedBranch) {
          const parsed = JSON.parse(storedBranch);
          if (Array.isArray(parsed) && parsed.length > 0) {
            setBranches(parsed);
            return;
          }
        }
        const res = await platformApi.get("/api/sparesInventory/branch/all");
        const list = res?.data?.response?.data || res?.data?.data || [];
        if (Array.isArray(list)) {
          setBranches(list);
        }
      } catch (err) {
        console.error("Error fetching branches:", err);
      }
    };
    fetchBranches();
  }, []);

  // 3. Fetch Master Bin Locations (for Permanent vs Temp check)
  // 3. Fetch Master Bin Locations (with branch mapping for Permanent vs Temp check)
  useEffect(() => {
    const fetchMasterBins = async () => {
      try {
        const res = await platformApi.post("/api/binLocation/get", {
          page: 1,
          size: 500,
        });
        const resData = res?.data?.response?.data || res?.data?.data || res?.data;
        let list = [];
        if (Array.isArray(resData?.binLocation)) {
          list = resData.binLocation.map((b) => (b.binLocation || "").trim()).filter(Boolean);
        } else if (Array.isArray(resData)) {
          list = resData.map((b) => (b.binLocation || "").trim()).filter(Boolean);
        }
        const rawList = Array.isArray(resData?.binLocation)
          ? resData.binLocation
          : Array.isArray(resData)
          ? resData
          : [];

        list = rawList
          .map((b) => ({
            branch: typeof b === "object" ? b.branch || b.Branch?.id || "" : "",
            binLocation: (typeof b === "object" ? b.binLocation || "" : b || "").trim(),
          }))
          .filter((b) => b.binLocation);

        if (list.length > 0) {
          setMasterBinLocations(list);
        }
      } catch (err) {
        console.error("Error fetching master bin locations:", err);
      }
    };
    fetchMasterBins();
  }, []);

  // 4. Fetch Users Map (to resolve creator ID into employee name)
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const res = await platformApi.get("/api/user");
        const userList =
          res?.data?.data?.users ||
          res?.data?.response?.data?.users ||
          res?.data?.data?.users ||
          res?.data?.response ||
          res?.data?.users ||
          res?.data ||
          [];
        if (Array.isArray(userList) && userList.length > 0) {
          const map = {};
          userList.forEach((u) => {
            const empName =
              u.profile?.employeeName ||
              u.EmployeeProfile?.employeeName ||
              u.name ||
              "";
            if (u.id && empName) {
              map[u.id] = empName;
            }
          });
          setUsersMap((prev) => ({ ...prev, ...map }));
        }
      } catch (err) {
        console.error("Error fetching users map:", err);
      }
    };
    fetchUsers();
  }, []);

  // Add logged in user to usersMap
  useEffect(() => {
    const empName = loginCredintials?.employeeName || loginCredintials?.profile?.employeeName;
    const uid = loginCredintials?.id || loginCredintials?.employeeId;
    if (uid && empName) {
      setUsersMap((prev) => ({
        ...prev,
        [uid]: empName,
      }));
    }
  }, [loginCredintials]);

  // Page limit dropdown menu
  const getMenu = () => {
    let size = count / 10 + (count % 10 ? 1 : 0);
    const menus = [];
    menus.push(10);
    size = size <= 10 ? size : 10;
    for (let itr = 2; itr <= size; itr++) menus.push(itr * 10);
    return menus.map((m) => (
      <Item
        onClick={() => {
          setLimit(m);
          setPage(1);
        }}
        key={m}
      >
        {m}
      </Item>
    ));
  };
  const menu = <Menu>{getMenu()}</Menu>;

  // 5. Fetch Historical Stock Inward Records
  const fetchRecords = useCallback(async () => {
    setLoading(true);
    try {
      const body = {
        page: 1,
        size: 500,
        searchString: "",
        branchId: activeBranchIds.length > 0 ? activeBranchIds : undefined,
      };

      const res = await platformApi.post("/api/stockCheck/inwardList", body);
      const resData = res?.data?.response?.data || res?.data?.data || res?.data;
      const list = resData?.stockInwards || [];
      const totalCount = resData?.count || 0;

      const isIdLike = (val) => /^[a-z0-9_-]{15,}$/i.test((val || "").trim());

      const mapped = list.map((item) => {
        let parsedInwardData = [];
        if (item.inwardData) {
          parsedInwardData =
            typeof item.inwardData === "string"
              ? JSON.parse(item.inwardData)
              : item.inwardData;
        } else if (Array.isArray(item.bins)) {
          parsedInwardData = item.bins;
        }

        const firstBinLocation =
          parsedInwardData?.[0]?.binLocation ||
          item.binLocation ||
          item.bins?.[0]?.binLocation ||
          "-";

        const rawCreatedBy = item.createdBy;
        const createdById =
          typeof rawCreatedBy === "string"
            ? rawCreatedBy
            : rawCreatedBy?.id || item.createdById || item.userId || "";

        let candidateName =
          item.createdByName ||
          item.createdBy?.profile?.employeeName ||
          item.createdBy?.name ||
          item.CreatedBy?.profile?.employeeName ||
          item.CreatedBy?.name ||
          item.createdByUser?.profile?.employeeName ||
          item.createdByUser?.name ||
          item.user?.name ||
          item.User?.name ||
          item.employeeName ||
          "";

        if (isIdLike(candidateName)) {
          candidateName = "";
        }

        const resolvedInwardedBy =
          candidateName ||
          item.User?.EmployeeProfile_User_profileToEmployeeProfile?.employeeName ||
          (createdById && usersMap[createdById]) ||
          (loginCredintials?.id && createdById === loginCredintials.id && (loginCredintials.employeeName || loginCredintials.profile?.employeeName)) ||
          (loginCredintials?.employeeId && createdById === loginCredintials.employeeId && (loginCredintials.employeeName || loginCredintials.profile?.employeeName)) ||
          (loginCredintials?.employeeName || loginCredintials?.profile?.employeeName || "-");

        let computedParts = 0;
        let computedQty = 0;
        parsedInwardData.forEach((bin) => {
          (bin.parts || []).forEach((p) => {
            computedParts += 1;
            computedQty += Number(p.quantity) || 0;
          });
        });

        return {
          id: String(item.id),
          inwardNo: item.inwardNo || `SI-${String(item.id).slice(-6)}`,
          inwardDate: item.inwardDate ? moment(item.inwardDate).format("DD/MM/YYYY") : "N/A",
          branch: item.branch || (item.Branch && item.Branch.id) || "",
          branchName: (item.Branch && item.Branch.name) || item.branchName || "Main Branch",
          noOfBinsCount: item.noOfBinsCount || parsedInwardData.length || 0,
          noOfPartsCount: item.noOfPartsCount || computedParts,
          totalQtyCount: item.totalQtyCount || computedQty,
          firstBinLocation,
          binLocationType: item.binLocationType || item.binType || undefined,
          createdById,
          inwardedByName: resolvedInwardedBy,
          inwardData: parsedInwardData,
          createdAt: item.createdAt || "",
        };
      });

      setRecords(mapped);
      setCount(totalCount || mapped.length);
    } catch (err) {
      console.error("Error fetching Stock Inward list:", err);
      message.error("Failed to fetch Stock Inward list");
      setRecords([]);
      setCount(0);
    } finally {
      setLoading(false);
    }
  }, [usersMap, loginCredintials, activeBranchIds]);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords, selectBranch]);

  // 6. Real-time instant search filter with branch filtering
  const filteredRecords = useMemo(() => {
    let result = records;
    if (activeBranchIds.length > 0 || activeBranchNames.length > 0) {
      result = result.filter((r) => {
        const matchesId = r.branch && activeBranchIds.includes(r.branch);
        const matchesName =
          r.branchName &&
          activeBranchNames.includes(r.branchName.trim().toLowerCase());
        return Boolean(matchesId || matchesName);
      });
    }

    const query = (searchQuery || "").trim().toLowerCase();
    if (!query) return result;

    return result.filter((r) => {
      const matchBin = r.firstBinLocation && r.firstBinLocation.toLowerCase().includes(query);
      const matchInwardedBy = r.inwardedByName && r.inwardedByName.toLowerCase().includes(query);
      const matchBranch = r.branchName && r.branchName.toLowerCase().includes(query);
      const matchInwardNo = r.inwardNo && r.inwardNo.toLowerCase().includes(query);

      return Boolean(matchBin || matchInwardedBy || matchBranch || matchInwardNo);
    });
  }, [records, searchQuery, activeBranchIds, activeBranchNames]);

  // Paginated slice
  const paginatedData = useMemo(() => {
    const start = (page - 1) * limit;
    return filteredRecords.slice(start, start + limit);
  }, [filteredRecords, page, limit]);

  // 7. Delete Record Handler
  const handleDeleteRecord = async (id) => {
    try {
      const res = await platformApi.delete(`/api/stockCheck/inward/${id}`);
      if (res?.data?.code === 200 || res?.status === 200) {
        message.success("Stock Inward record deleted successfully");
        fetchRecords();
      } else {
        message.error(res?.data?.message || "Failed to delete Stock Inward record");
      }
    } catch (err) {
      console.error("Error deleting stock inward:", err);
      message.error(err?.response?.data?.message || "Error deleting Stock Inward record");
    }
  };

  // Open Add
  const handleOpenAdd = () => {
    setEditRecord(null);
    setIsInwardModalOpen(true);
  };

  // Open Edit / Modify
  const handleOpenEdit = (record) => {
    setEditRecord(record);
    setIsInwardModalOpen(true);
  };

  // Open View
  const handleOpenView = (record) => {
    setViewRecord(record);
    setIsViewModalOpen(true);
  };

  return (
    <>
      <div className="accessories-head">
        <Title style={{ width: "60%" }} level={4}>
          Stock Checking [{filteredRecords.length}]
          <span style={{ marginLeft: "1rem" }}>
            <Dropdown overlay={menu} trigger={["click"]}>
              <Button>
                {limit} &nbsp;
                <Icon type="down" />
              </Button>
            </Dropdown>
          </span>
        </Title>

        <Search
          style={{ width: "30%" }}
          placeholder="Search Stock Checking"
          onSearch={(event) => {
            setPage(1);
            setSearchQuery(event);
          }}
          onChange={(event) => {
            setSearchQuery(event.target.value);
            setPage(1);
          }}
        />

        <Button
          onClick={handleOpenAdd}
          style={{ margin: "0 3%", width: "20%" }}
          type="primary"
          disabled={!createAccess}
        >
          Add Stock Checking
        </Button>
      </div>

      <StockCheckTable
        dataSource={paginatedData}
        loading={loading}
        onView={handleOpenView}
        onModify={handleOpenEdit}
        onDelete={handleDeleteRecord}
        createAccess={createAccess}
        modifyAccess={modifyAccess}
        deleteAccess={deleteAccess}
        masterBinLocations={masterBinLocations}
        pagination={{
          onChange: (p) => setPage(p),
          pageSize: limit,
          current: page,
          total: filteredRecords.length,
          showQuickJumper: true,
        }}
      />

      {/* Stock Inward Add / Modify Modal (with Hardware Barcode Scanner) */}
      <StockInwardModal
        visible={isInwardModalOpen}
        onClose={() => setIsInwardModalOpen(false)}
        onSuccess={() => {
          fetchRecords();
          setIsInwardModalOpen(false);
        }}
        editData={editRecord}
        branches={branches}
        currentBranchId={activeBranchIds[0] || (branches[0] && branches[0].id)}
        masterBinLocations={masterBinLocations}
      />

      {/* Stock Inward Details View Modal */}
      <StockInwardViewModal
        visible={isViewModalOpen}
        onClose={() => setIsViewModalOpen(false)}
        data={viewRecord}
        masterBinLocations={masterBinLocations}
      />
    </>
  );
};

export default StockCheck;
